import type { Api } from "../../shared/api";
import type { FanoutView, SubagentTaskView } from "../../shared/subagents";
import { api } from "../lib/api";
import { fmtDateTime, fmtUsd } from "../lib/format";
import { useResource } from "../lib/store";
import { Badge, Section } from "./ui";

type Fanouts = Api["GET /api/subagents"]["res"];

const TASK_LABEL: Record<SubagentTaskView["status"], string> = {
  queued: "En attente",
  running: "En cours",
  done: "Terminé",
  failed: "Échec",
  stopped: "Arrêté",
};
const TASK_TONE: Record<SubagentTaskView["status"], "neutral" | "accent" | "success" | "danger"> = {
  queued: "neutral",
  running: "accent",
  done: "success",
  failed: "danger",
  stopped: "neutral",
};

/** Activity page: the agent's recent fan-outs of parallel sub-agents, refreshed live while they run. */
export function SubagentsPanel({ now }: { now: number }) {
  const fanouts = useResource<Fanouts>("subagents", (signal) => api("GET /api/subagents", { signal }), {
    refreshOn: (e) => (e.type === "turn.tool" && e.name === "parallel_tasks") || e.type === "turn.done",
  });
  const list = fanouts.data?.fanouts ?? [];
  if (!list.length) return null;
  return (
    <Section title="Sous-agents en parallèle">
      <ul className="space-y-2">
        {list.map((f) => (
          <FanoutRow key={f.id} fanout={f} now={now} />
        ))}
      </ul>
    </Section>
  );
}

function FanoutRow({ fanout, now }: { fanout: FanoutView; now: number }) {
  const done = fanout.tasks.filter((t) => t.status === "done").length;
  return (
    <li data-testid="fanout-row" className="min-w-0 rounded-xl border border-line bg-elev px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 text-[14px] font-medium wrap-anywhere">{fanout.goal}</span>
        <Badge tone={fanout.status === "running" ? "accent" : fanout.status === "done" ? "success" : "danger"}>
          {fanout.status === "running" ? `${done}/${fanout.tasks.length} terminés` : fanout.status === "done" ? "Terminé" : "Échec"}
        </Badge>
        <span className="ml-auto text-[12.5px] text-muted tabular-nums">
          {fmtUsd(fanout.costUsd)} <span className="text-subtle">/ {fmtUsd(fanout.capUsd)} max</span>
        </span>
      </div>
      <p className="mt-0.5 text-[12px] text-subtle">{fmtDateTime(fanout.createdAt, now)}</p>
      <ul className="mt-2 space-y-1">
        {fanout.tasks.map((t) => (
          <li key={t.idx} data-testid="subagent-task" className="flex min-w-0 flex-wrap items-center gap-2 text-[12.5px]">
            <Badge tone={TASK_TONE[t.status]}>{TASK_LABEL[t.status]}</Badge>
            <span className="min-w-0 wrap-anywhere">{t.title}</span>
            <span className="text-subtle">{t.tier === "fast" ? "extraction rapide" : "analyse"}</span>
            {t.error ? <span className="text-subtle wrap-anywhere">· {t.error}</span> : null}
          </li>
        ))}
      </ul>
    </li>
  );
}
